import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ScrollStateService {
  // =========================
  // UI visibility state
  // =========================
  private _navbarHidden = signal(false);
  private _footerHidden = signal(false);
  private _dockHidden = signal(false);

  navbarHidden = this._navbarHidden;
  footerHidden = this._footerHidden;
  dockHidden = this._dockHidden;

  setNavbarHidden(v: boolean): void {
    this._navbarHidden.set(v);
  }

  setFooterHidden(v: boolean): void {
    this._footerHidden.set(v);
  }

  setDockHidden(v: boolean): void {
    this._dockHidden.set(v);
  }

  resetVisibility(): void {
    this.setNavbarHidden(false);
    this.setDockHidden(false);
    this.setFooterHidden(false);
  }

  // =========================
  // Scroll behavior config
  // =========================
  private readonly DIR_THRESHOLD = 4;
  private readonly TOP_PIN_PX = 0;
  private readonly BOTTOM_PIN_PX = 2;

  // =========================
  // Direction tracking per key
  // =========================
  private _dockLastTopByKey = new Map<string, number>();
  private _navLastTopByKey = new Map<string, number>();
  private _footerLastTopByKey = new Map<string, number>();

  // =========================
  // Persisted scrollTop per logical key
  // e.g. "blog-list", "blog-details"
  // =========================
  private _scrollTopByKey = new Map<string, number>();

  // Optional legacy list tracking
  private _listScrollTop = signal(0);
  private _listLastTop = signal(0);

  private readonly DEBUG = false;

private debug(action: string, payload?: Record<string, unknown>): void {
  if (!this.DEBUG) return;
  console.log(`[ScrollStateService] ${action}`, payload ?? {});
}

private describeEl(el: HTMLElement | null): string {
  if (!el) return 'NULL';

  const id = el.id ? `#${el.id}` : '';
  const classes = el.classList?.length ? '.' + Array.from(el.classList).join('.') : '';
  const inDarkContainer = !!el.closest('#dark-container-blog');

  return `${el.tagName.toLowerCase()}${id}${classes}${inDarkContainer ? ' [dark]' : ' [light]'}`;
}

  updateListScroll(top: number): void {
    const last = this._listLastTop();
    const dy = top - last;

    this._listScrollTop.set(top);
    this._listLastTop.set(top);

    void dy;
  }

  // =========================
  // Persisted scroll position API
  // =========================
setScrollTop(key: string, top: number): void {
  this._scrollTopByKey.set(key, top);

  this.debug('SET key top', {
    key,
    top
  });
}

  getScrollTop(key: string): number {
    return this._scrollTopByKey.get(key) ?? 0;
  }

saveScrollPosition(key: string, el: HTMLElement | null): void {
  if (!el) {
    this.debug('SAVE skipped (null element)', { key });
    return;
  }

  const top = el.scrollTop;
  this._scrollTopByKey.set(key, top);

  this.debug('SAVE', {
    key,
    top,
    element: this.describeEl(el)
  });
}

restoreScrollPosition(key: string, el: HTMLElement | null): void {
  if (!el) {
    this.debug('RESTORE skipped (null element)', { key });
    return;
  }

  const top = this.getScrollTop(key);

  this.debug('RESTORE before', {
    key,
    storedTop: top,
    currentTop: el.scrollTop,
    element: this.describeEl(el)
  });

  el.scrollTop = top;

  this.debug('RESTORE after', {
    key,
    appliedTop: el.scrollTop,
    element: this.describeEl(el)
  });
}

restoreScrollPositionAndResetKey(key: string, el: HTMLElement | null): void {
  if (!el) {
    this.debug('RESTORE+RESET skipped (null element)', { key });
    return;
  }

  const top = this.getScrollTop(key);

  this.debug('RESTORE+RESET before', {
    key,
    storedTop: top,
    currentTop: el.scrollTop,
    element: this.describeEl(el)
  });

  el.scrollTop = top;
  this.resetScrollKey(key, top);

  this.debug('RESTORE+RESET after', {
    key,
    appliedTop: el.scrollTop,
    resetTop: top,
    element: this.describeEl(el)
  });
}

  clearScrollPosition(key: string): void {
    this._scrollTopByKey.delete(key);
  }

  clearAllScrollPositions(): void {
    this._scrollTopByKey.clear();
  }

  // =========================
  // Direction logic
  // =========================
  updateDockByScroll(
    key: string,
    top: number,
    scrollHeight: number,
    clientHeight: number
  ): void {
    const last = this._dockLastTopByKey.get(key) ?? top;
    const dy = top - last;
    this._dockLastTopByKey.set(key, top);

    const maxScroll = scrollHeight - clientHeight;

    if (top <= this.TOP_PIN_PX) {
      this.setDockHidden(false);
      return;
    }

    if (top >= maxScroll - this.BOTTOM_PIN_PX) {
      this.setDockHidden(false);
      return;
    }

    if (dy > this.DIR_THRESHOLD) {
      this.setDockHidden(true);
    } else if (dy < -this.DIR_THRESHOLD) {
      this.setDockHidden(false);
    }
  }

  updateNavByScroll(
    key: string,
    top: number,
    scrollHeight: number,
    clientHeight: number
  ): void {
    const last = this._navLastTopByKey.get(key) ?? top;
    const dy = top - last;
    this._navLastTopByKey.set(key, top);

    const maxScroll = scrollHeight - clientHeight;

    if (top <= this.TOP_PIN_PX) {
      this.setNavbarHidden(false);
      return;
    }

    if (top >= maxScroll - this.BOTTOM_PIN_PX) {
      this.setNavbarHidden(false);
      return;
    }

    if (dy > this.DIR_THRESHOLD) {
      this.setNavbarHidden(false);
    } else if (dy < -this.DIR_THRESHOLD) {
      this.setNavbarHidden(true);
    }
  }

  updateFooterByScroll(
    key: string,
    top: number,
    scrollHeight: number,
    clientHeight: number
  ): void {
    const last = this._footerLastTopByKey.get(key) ?? top;
    const dy = top - last;
    this._footerLastTopByKey.set(key, top);

    const maxScroll = scrollHeight - clientHeight;

    if (top <= this.TOP_PIN_PX) {
      this.setFooterHidden(false);
      return;
    }

    if (top >= maxScroll - this.BOTTOM_PIN_PX) {
      this.setFooterHidden(false);
      return;
    }

    if (dy > this.DIR_THRESHOLD) {
      this.setFooterHidden(true);
    } else if (dy < -this.DIR_THRESHOLD) {
      this.setFooterHidden(false);
    }
  }

  // =========================
  // Key reset helpers
  // =========================
resetScrollKey(key: string, top = 0): void {
  this._dockLastTopByKey.set(key, top);
  this._navLastTopByKey.set(key, top);
  this._footerLastTopByKey.set(key, top);

  this.debug('RESET direction keys', {
    key,
    top
  });
}

  resetAllDirectionKeys(top = 0): void {
    for (const key of this._dockLastTopByKey.keys()) {
      this._dockLastTopByKey.set(key, top);
    }

    for (const key of this._navLastTopByKey.keys()) {
      this._navLastTopByKey.set(key, top);
    }

    for (const key of this._footerLastTopByKey.keys()) {
      this._footerLastTopByKey.set(key, top);
    }
  }

  // =========================
  // Binding helper
  // =========================
bindShellHideOnScroll(
  selector: string,
  key: string,
  opts: { dock?: boolean; nav?: boolean; footer?: boolean },
  restorePosition = false
): void {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) {
    this.debug('BIND skipped (element not found)', { selector, key });
    return;
  }

  const anyEl = el as HTMLElement & {
    __shellScrollHandler__?: EventListener;
  };

  if (anyEl.__shellScrollHandler__) {
    el.removeEventListener('scroll', anyEl.__shellScrollHandler__);
    this.debug('BIND removed previous handler', {
      selector,
      key,
      element: this.describeEl(el)
    });
  }

  if (restorePosition) {
    const top = this.getScrollTop(key);

    this.debug('BIND restorePosition=true', {
      selector,
      key,
      storedTop: top,
      beforeTop: el.scrollTop,
      element: this.describeEl(el)
    });

    el.scrollTop = top;
    this.resetScrollKey(key, top);

    this.debug('BIND restored', {
      selector,
      key,
      afterTop: el.scrollTop,
      element: this.describeEl(el)
    });
  } else {
    this.setScrollTop(key, el.scrollTop);
    this.resetScrollKey(key, el.scrollTop);

    this.debug('BIND restorePosition=false', {
      selector,
      key,
      initialTop: el.scrollTop,
      element: this.describeEl(el)
    });
  }

  const handler: EventListener = () => {
    const top = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    this.setScrollTop(key, top);

    this.debug('SCROLL event', {
      key,
      top,
      scrollHeight,
      clientHeight,
      element: this.describeEl(el)
    });

    if (opts.dock) {
      this.updateDockByScroll(key, top, scrollHeight, clientHeight);
    }

    if (opts.nav) {
      this.updateNavByScroll(key, top, scrollHeight, clientHeight);
    }

    if (opts.footer) {
      this.updateFooterByScroll(key, top, scrollHeight, clientHeight);
    }
  };

  anyEl.__shellScrollHandler__ = handler;
  el.addEventListener('scroll', handler, { passive: true });

  this.debug('BIND attached handler', {
    selector,
    key,
    opts,
    restorePosition,
    element: this.describeEl(el)
  });
}

unbindShellHideOnScroll(selector: string): void {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) {
    this.debug('UNBIND skipped (element not found)', { selector });
    return;
  }

  const anyEl = el as HTMLElement & {
    __shellScrollHandler__?: EventListener;
  };

  if (anyEl.__shellScrollHandler__) {
    el.removeEventListener('scroll', anyEl.__shellScrollHandler__);
    delete anyEl.__shellScrollHandler__;

    this.debug('UNBIND success', {
      selector,
      element: this.describeEl(el)
    });
  } else {
    this.debug('UNBIND no handler present', {
      selector,
      element: this.describeEl(el)
    });
  }
}
}
